import React, { useCallback } from "react";
import { gql, useQuery, useSubscription } from "@apollo/client";
import {
  MyTransactionsQueryQuery,
  MyTransactionsQueryQueryVariables,
  Transaction
} from "../../../schemaTypes";
import { newTransactionSubscription } from "../../Admin/Transaction/FindTransactions";

export const findTransactionsQuery = gql`
  query MyTransactionsQuery(
    $offset: Int!
    $limit: Int!
    $duration: String
    $filterBy: String
    $typeFilter: String
    $startDate: String
    $endDate: String
    $searchText: String
  ){
    myTransactions(
      offset: $offset
      limit: $limit
      duration: $duration
      filterBy: $filterBy
      typeFilter: $typeFilter
      startDate: $startDate
      endDate: $endDate
      searchText: $searchText
    ){
      createdAt
      transactions {
        id
        amount
        charge
        reference
        type
        status
        currency
        transferTypes
        # wallet {
        #   id
        #   balance
        #   createdAt
        #   updatedAt
        # }
        # recipientWallet {
        #   id
        #   balance
        #   currency
        #   owner{
        #     admin{
        #       merchantProfile{
        #         companyName
        #       }
        #     }
        #   }
        #   createdAt
        #   updatedAt
        # }
        offer{
          id
          title
          description
          bannerUrl
          category {
            id
            name
            imgSrc
            createdBy{
              id
              role
            }
            createdAt
            updatedAt
          }
          offerType
          discountType
          discountValue
          partnerVoucher
          referralUrl
          status
          usageLimitPerUser
          totalUsageLimit
          startDate
          endDate
          community{
            id
            name
            alias
          }
          redemptions
          revenueGenerated
          clickThroughs
          featured
          createdBy{
            id
            role
            admin {
              id
              shortId
              firstname
              lastname
              email
              phone
              merchantProfile {
                id
                businessName
                businessType
                regCountry
                businessRegNumber
                taxId
                address
                addressAlt
                city
                country
                postcode
                companyName
                username
                industry {
                  id
                  name
                  createdBy{
                    id
                    role
                  }
                  createdAt
                  updatedAt
                }
                companyLocation
                logoUrl
                bannerUrl
                description
                commissionRate
                createdAt
                updatedAt
              }
              createdAt
              updatedAt
            }
          }
          # njoyCode {
          #   id
          #   code
          #   redeemedAt
          #   createdAt
          #   updatedAt
          # }
          createdAt
          updatedAt
        }
        njoyCode
        partnerVoucher
        user {
          id
          # createdAt
          # updatedAt
        }
        createdAt
        updatedAt
      }
    }
  }
`;

newTransactionSubscription;

export interface WithFindMyTransactions {
  myTransactions: Transaction[];
  loading: boolean;
  loadMore: () => void;
  hasMoreTransactions: boolean;
}

interface Props {
  variables: MyTransactionsQueryQueryVariables;
  children: (data: WithFindMyTransactions) => React.ReactElement | null;
}

export const MyTransactions: React.FC<Props> = ({ children, variables }) => {
  const { data, loading, fetchMore
    // , subscribeToMore 
  } = useQuery<
    MyTransactionsQueryQuery,
    MyTransactionsQueryQueryVariables
  >(findTransactionsQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  // Subscription for new transactions
  useSubscription(newTransactionSubscription, {
    onSubscriptionData: ({ client, subscriptionData }) => {
      if (!subscriptionData.data) return;
      client.cache.modify({
        fields: {
          myTransactions(existing = []) {
            return [...existing, subscriptionData.data.newTransaction];
          }
        }
      });
    }
  });

  let myTransactions: Transaction[] = [];
  if (data && data.myTransactions) {
    myTransactions = data.myTransactions.reduce(
      (acc, group) => acc.concat(group.transactions as Transaction[]),
      [] as Transaction[]
    );
  }

  let hasMoreTransactions = myTransactions.length % variables.limit === 0;
  if (myTransactions.length <= variables.offset) {
    hasMoreTransactions = false;
  }

  const loadMore = useCallback(() => {
    fetchMore({
      variables: {
        ...variables,
        offset: myTransactions.length
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (
          !fetchMoreResult ||
          !fetchMoreResult.myTransactions ||
          !prev ||
          !prev.myTransactions
        ) {
          return prev;
        }
        return {
          ...prev,
          myTransactions: [
            ...prev.myTransactions,
            ...fetchMoreResult.myTransactions
          ]
        };
      }
    });
  }, [fetchMore, variables, myTransactions.length]);

  return children({
    hasMoreTransactions,
    myTransactions,
    loading,
    loadMore,
  });
};